<template>
  <div>
    <ValidationObserver ref="validator" slim v-slot="{ handleSubmit, passed }">
      <h3>{{ $tc('meter-box.electricalMeter', 1) }}</h3>
      <div class="row">
        <div v-if="isHouseSelected" class="col-xs-12 col-sm-4 col-md-4 lib__field">
          <p class="lib__field__label">{{ $t('meter.form.count') }}</p>
          <h4>
            {{ meter.count }}
          </h4>
        </div>
        <div v-if="!isHouseSelected" class="col-xs-12 col-sm-4 col-md-4">
          <number-box :class="isHouseSelected ? 'lib__field--disabled' : ''" :label="$t('meter.form.count')" :min="1"
            :step="1" required v-model="meter.count" :disabled="isHouseSelected"></number-box>
        </div>

        <div v-if="meter.package && (meter.package.key === 'Essentiel' || meter.package.key === 'Confort')"
          class="col-xs-12 col-sm-8 col-md-6 lib__field">
          <p class="lib__field__label">{{ $t('meter.form.connectionType') }}</p>
          <div class="lib__field__group lib__no-margin">
            <div class="lib__field lib__field--radio lib__no-margin"
              :class="{ 'lib__field--disabled': meter.package && meter.package.key === 'Confort' }">
              <input @click="meterConnectionTypeChange('monophase')" class="lib__field__input" type="radio"
                :disabled="meter.package && meter.package.key === 'Confort'" name="meterConnectionType" id="monophase"
                :checked="meter.connectionType === 'monophase'">
              <label class="lib__field__label" for="monophase">{{
                $t('meter.phase.monophase') }}</label>
            </div>
            <div class="lib__field lib__field--radio lib__no-margin"
              :class="{ 'lib__field--disabled': meter.package && meter.package.key === 'Confort' }">
              <input @click="meterConnectionTypeChange('triphase')" class="lib__field__input" type="radio"
                :disabled="meter.package && meter.package.key === 'Confort'" name="meterConnectionType" id="triphase"
                :checked="meter.connectionType === 'triphase'">
              <label class="lib__field__label" for="triphase">{{
                $t('meter.phase.triphase') }}</label>
            </div>
            <small v-if="meter.package && meter.package.key === 'Confort'" class="lib__text-gray-500 lib__no-margin-bottom">{{
              $t('meter.form.monophaseInfoLabel') }}</small>
          </div>
        </div>

      </div>

      <div class="row lib__field">
        <label class="lib__field__label">{{ $t('meter.form.choosePackage') }}</label>
        <div
          class="lib__field__select lib__field__select--custom lib__field__select--custom-alt lib__field__select__custom--infobox lib__field__select__custom--fullsize">
          <component class="lib__infobox-list ul" :is="'ul'">
            <info-box v-if="!meter.customPower" :item="true" :level="'primary'" class="lib__field__select__item--active">
              <div class="lib__infobox__content lib__no-padding">
                <div class="lib__infobox__header__title">
                  <h3 class="lib__infobox__megatitle">
                    <span class="lib__text-gray-500 lib__text-weight-400 lib__text-no-uppercase">
                      {{ $t('meter.electricityPackage.packageLabel') }}
                    </span>
                    <span>{{ meter.package && meter.package.key }}</span>
                  </h3>
                </div>
                <p><span class="lib__text-large">{{ $t('meter.maximumPowerGuaranteedOf') }} <b class="lib__text-theme">{{
                  meter.package && meter.package.maximumPower.kiloVoltAmpere }}
                      kVA</b></span></p>
              </div>
            </info-box>
            <info-box v-if="meter.customPower" :item="true" :level="'primary'" class="lib__field__select__item--active">
              <div class="lib__infobox__content lib__no-padding">
                <div class="lib__infobox__header__title">
                  <h3 class="lib__infobox__megatitle">
                    <span class="lib__text-gray-500 lib__text-weight-400 lib__text-no-uppercase">
                      {{ $t('meter.electricityPackage.packageLabel') }}
                    </span>
                    <span>{{ $t('meter.electricityPackage.69+') }}</span>
                  </h3>
                </div>
              </div>
            </info-box>
            <li @click="selectPackage(meterPackage)" v-for="meterPackage in meterElectricityPackages">
              <b class="lib__text-uppercase">{{ meterPackage.key }}</b>
              <div>{{ $t('meter.maximumPowerGuaranteedOf') }} <b>{{ meterPackage.maximumPower.kiloVoltAmpere }} kVA</b>
              </div>
            </li>
            <li @click="customPowerClick()" class="lib__field__select__item--other">
              <a class="lib__btn lib__btn--link lib__btn--secondary"><span>{{
                $t('meter.form.needMorePower') }}</span></a>
            </li>
          </component>
        </div>
      </div>
      <div v-if="meter.customPower" class="row">
        <number-box :label="$t('meter.form.maximumPowerWantedKVA')" :moreThan="69.3" :max="99999" :maxLength="6" required
          v-model="meter.wantedPower"></number-box>
      </div>
      <collapsable-text-area :max='500' :add-label="$t('meter.form.remarks')" :label="$t('meter.form.remarks')"
        v-model="meter.remarks"></collapsable-text-area>
      <div class="lib__text-center">
        <a :class="{ 'lib__btn--disabled': !passed }" @click="handleSubmit(submit)"
          class="lib__btn lib__btn--primary lib__btn--rounded" href="#">
          <slot>
            <i class="fas fa-check"></i>
            <span>{{ $t('common.confirm') }}</span>
          </slot>
        </a>
      </div>
    </ValidationObserver>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { ElectricityPowerSupplyType, PackageDTO, PackagedElectricityMeter } from '@/models'
import PowerSelectBox from '@/components/inputs/Configuration/PowerSelectBox.vue'
import { helper } from '@/services'

@Component({
  components: {
    PowerSelectBox
  },
  name: 'cmh-electricity-package-popup-form'
})
export default class CmhElectricityPackagePopupForm extends Vue {
  //#region [Property]
  @Prop({ type: Object, required: true }) public electricityMeter!: PackagedElectricityMeter
  @Prop({ type: Object, required: true }) public defaultPackagedElectricityMeter!: PackagedElectricityMeter
  @Prop({ type: Array, required: true }) public meterElectricityPackages!: PackageDTO[]
  @Prop({ type: Boolean, required: true }) public isHouseSelected!: boolean
  //#endregion

  //#region [Data]

  public meter: PackagedElectricityMeter = {
    count: null,
    connectionType: null,
    remarks: null,
    package: null,
    customPower: false,
    wantedPower: null,
    destination: null
  }

  //#endregion

  //#region [Method]
  public mounted() {
    
    this.meter = this.electricityMeter ? helper.clone(this.electricityMeter) : helper.clone(this.defaultPackagedElectricityMeter)

    if (this.isHouseSelected) {
      // This line forced a first validation. Necessary in case where isHouseSelected === true because no input field
      this.$nextTick(() => this.$validatorObs.validate())
    }
  }

  meterConnectionTypeChange(connectionType: string) {
    switch (connectionType) {
      case 'monophase':
        this.meter.connectionType = ElectricityPowerSupplyType.monophase
        break
      case 'triphase':
        this.meter.connectionType = ElectricityPowerSupplyType.triphase
        break
      default:
        this.meter.connectionType = null
        break
    }
  }

  selectPackage(selectedPackage: PackageDTO) {
    if (selectedPackage.key === 'Confort') {
      this.meter.connectionType = ElectricityPowerSupplyType.triphase
    } else if (selectedPackage.key === 'Essentiel') {
      this.meter.connectionType = ElectricityPowerSupplyType.monophase
    } else {
      this.meter.connectionType = null
    }
    this.meter.customPower = false
    this.meter.wantedPower = null
    this.meter.package = selectedPackage
  }

  customPowerClick() {
    this.meter.customPower = true
    this.meter.package = {
      key: '69+',
      maximumPower: {
        kiloVoltAmpere: -1,
        voltAmpere: -1
      }
    }
  }

  public submit() {
    this.$emit('submit', this.meter)
  }
  //#endregion

  //#region [Watch]
  @Watch('electricityMeter')
  public onChange() {
    this.meter = helper.clone(this.electricityMeter)
  }

  //#endregion
}
</script>
